'use client';

import { useMemo } from 'react';

import { Distance } from '@/components/distance';
import { PhysicianData } from '@/features/profile/profile.types';
import { useProviderLocation } from '@/hooks/use-provider-location';
import { NeuImage } from '@hcahealthcaredpx/neutron-react';

import { useListingContext } from '../../hooks';
import styles from '../../listing.module.css';
import { getPhysicianFormattedName } from '../../utils';
import { AddressSection } from './address';
import { BookAppointmentSection } from './book-appointment';
import { FooterSection } from './footer';
import { HeaderSection } from './header';
import { ProceduresAndConditionsSection } from './procedures-and-conditions';

export const DoctorCard = ({
  physician,
  physicianIndex,
  totalCount,
}: {
  physician: PhysicianData;
  physicianIndex: number;
  totalCount: number;
}): JSX.Element => {
  const {
    providerLocations,
    providerSpecialties,
    mhdParticipant,
    telehealthProvider,
    physicianPhotoUrl,
    physicianFirstName,
    physicianLastName,
    physicianDesignation,
    physicianMiddleInitial,
    proceduresAndConditions,
    urlTitle,
    onlineSchedulingUrl,
    physicianNpi,
  } = physician;

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';

  const getProviderActiveLocation = useProviderLocation();
  const location = getProviderActiveLocation(providerLocations, physicianNpi);
  const specialtyFromPathParam = pathname.split('/specialty/')[1]?.split('/')[0].replace(/-/g, ' ');
  const { appliedFilters } = useListingContext();

  const displaySpecialty = useMemo(() => {
    const primaryDoctorSpecialty = providerSpecialties?.find((spec) => spec.primary);
    const specialtyFromFilter = providerSpecialties?.find((spec) => {
      return (
        spec.providerSpecialtyId === appliedFilters?.specialtyId ||
        spec.specialty.toLowerCase() === appliedFilters?.specialty?.toLowerCase() ||
        spec.specialty.toLowerCase() === specialtyFromPathParam?.toLowerCase()
      );
    });

    if (specialtyFromFilter) {
      return specialtyFromFilter.specialty;
    }
    return primaryDoctorSpecialty ? primaryDoctorSpecialty.specialty : '';
  }, [
    providerSpecialties,
    appliedFilters.specialtyId,
    appliedFilters.specialty,
    specialtyFromPathParam,
  ]);

  const formattedName = getPhysicianFormattedName({
    firstName: physicianFirstName,
    middleName: physicianMiddleInitial || '',
    lastName: physicianLastName,
    designation: physicianDesignation,
  });

  const phoneNumber = location.phone || location.relatedContactCenterPhoneNumber;

  return (
    <div className="py-3 px-2">
      <div className="d-flex justify-content-start">
        <div className="col-lg-2 col-md-2 col-xs-4 col-sm-3">
          <div className={styles.imageWrapper}>
            <NeuImage
              alt={`${physicianFirstName} ${physicianLastName}`}
              elevation={0}
              gradient={0}
              overlay={0}
              // Todo: Add a default image if none is provided
              src={physicianPhotoUrl}
              aria-hidden="true"
            />
          </div>
        </div>
        <HeaderSection
          physicianFullNameAndDesignation={formattedName}
          urlTitle={urlTitle}
          distance={location?.distance}
          physicianIndex={physicianIndex}
          totalCount={totalCount}
          specialty={displaySpecialty}
          redirectProfileState={{
            activeLocation: location,
            providerLocations,
          }}
        />
      </div>
      {location?.distance && (
        <div className="row d-lg-none p-1">
          <div className="col-3">
            <Distance distance={location?.distance} />
          </div>
        </div>
      )}
      <div className="row">
        <AddressSection
          activeLocation={location}
          providerLocations={providerLocations}
          providerId={physicianNpi}
        />
        <div className="col-lg-8 col-md-8">
          <div className="row">
            <ProceduresAndConditionsSection
              proceduresAndConditions={proceduresAndConditions || []}
              urlTitle={urlTitle}
              physicianName={`${physicianFirstName} ${physicianLastName}`}
              acceptsNewPatients={location?.acceptsNewPatients}
              telehealthProvider={telehealthProvider}
            />

            <BookAppointmentSection
              location={location}
              mhdParticipant={mhdParticipant}
              onlineSchedulingUrl={onlineSchedulingUrl}
              urlTitle={urlTitle}
              phoneNumber={phoneNumber}
              physicianName={formattedName}
              redirectProfileState={{
                activeLocation: location,
                providerLocations,
              }}
              physicianIndex={physicianIndex}
            />
          </div>
        </div>
        <FooterSection
          acceptsNewPatients={location?.acceptsNewPatients}
          telehealthProvider={telehealthProvider}
        />
      </div>
    </div>
  );
};
